import { Modal, modalWidth } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import useItemSku from "Catalog/Preview/Popup/ProductPopup/useItemSku"
import Allergens from "Catalog/Preview/shared/components/Allergens"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import { IProduct } from "shared/hubrise/models"

import {
  Close,
  Description,
  Details,
  Edit,
  Footer,
  Name,
  Title,
  Price,
  StyledPopup,
  Content,
  Header,
  StyledTags,
  DescriptionAndAllergens,
} from "../Styles"
import Image from "../shared/components/Image"
import OptionList from "../shared/components/OptionList"
import Sticky from "../shared/components/Sticky"

import SkuSelector from "./SkuSelector"

interface ProductPopupProps {
  product: IProduct
  rootElement: HTMLDivElement | undefined
}

const ProductPopup = ({ product, rootElement }: ProductPopupProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const $modalRef = React.useRef<HTMLDivElement>(null)
  const $contentRef = React.useRef<HTMLDivElement>(null)
  const $stickyHeaderRef = React.useRef<HTMLDivElement>(null)

  const { catalogNavigation } = useCatalogNavigation()
  const editPath = catalogNavigation.editPath("product", product.id)

  const { sku, optionLists, optionNames, price, setSkuName, setOptionNames } = useItemSku(product)

  const setSkuNameAndScroll = React.useCallback(
    (name: string) => {
      setSkuName(name)

      // Scroll the content so that it aligns just below the modal sticky header
      const { height: stickyHeaderHeight } = $stickyHeaderRef.current!.getBoundingClientRect()
      const { top: currentTop } = $contentRef.current!.getBoundingClientRect()
      const scrollOffset = currentTop - stickyHeaderHeight
      $modalRef.current!.scrollBy({ top: scrollOffset, behavior: "smooth" })
    },
    [setSkuName],
  )

  return (
    <Modal
      ref={$modalRef}
      rootElement={rootElement}
      modalWidth={modalWidth.xlarge}
      renderContent={({ close }) => (
        <StyledPopup>
          <Image productOrDeal={product} />

          <Details>
            <Sticky position="top" ref={$stickyHeaderRef} $scrollableDivRef={$modalRef}>
              <Header>
                <Title>
                  <Name>{product.name}</Name>
                  <StyledTags tags={product.tags} withToolTip={true} />
                  <Price dangerouslySetInnerHTML={{ __html: price.toHumanString() }} />
                </Title>

                <SkuSelector product={product} skuName={sku.name} setSkuName={setSkuNameAndScroll} />
              </Header>
            </Sticky>

            <Content ref={$contentRef}>
              <DescriptionAndAllergens>
                <Description dangerouslySetInnerHTML={{ __html: product.description }} />
                <Allergens allergens={product.nutrition?.allergens ?? null} />
              </DescriptionAndAllergens>

              {optionLists.map((optionList) => (
                <OptionList
                  key={optionList.id}
                  optionList={optionList}
                  optionNames={optionNames(optionList.name)}
                  setOptionNames={(names) => setOptionNames(optionList.name, names)}
                />
              ))}
            </Content>

            <Sticky position="bottom" $scrollableDivRef={$modalRef}>
              <Footer>
                <Edit onClick={() => navigate(editPath)}>{t("preview.popup.product.edit")}</Edit>
                <Close onClick={close}>{t("general.close")}</Close>
              </Footer>
            </Sticky>
          </Details>
        </StyledPopup>
      )}
      onClose={() => navigate(catalogNavigation.previewPath("all"))}
    />
  )
}

export default ProductPopup
